/**
 * Logs the user out after a period of inactivity.
 *
 * Mirrors the tradiepass-web implementation. The last-activity timestamp
 * lives in localStorage so activity in any open tab keeps every tab's
 * session alive, and a tab reopened after the timeout is caught
 * immediately. Activity writes are throttled so mousemove/scroll don't
 * hammer localStorage.
 */

// Everyone signed into the CMS is an administrator, so the whole app gets
// the short leash (per product: 30 minutes of inactivity).
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000

const ACTIVITY_KEY = 'LAST_ACTIVITY_AT'
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
]
const WRITE_THROTTLE_MS = 5000
const CHECK_INTERVAL_MS = 30000

function readLastActivity() {
  const raw = localStorage.getItem(ACTIVITY_KEY)
  const ts = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(ts) ? ts : null
}

/**
 * Starts idle tracking. Returns a stop() function; call it when the layout
 * that started tracking is destroyed.
 *
 * @param {{ onIdle: Function, timeoutMs?: number }} options
 */
export function startIdleLogout({ onIdle, timeoutMs = IDLE_TIMEOUT_MS }) {
  let stopped = false
  let lastWrite = 0

  const touch = () => {
    const now = Date.now()
    if (now - lastWrite < WRITE_THROTTLE_MS) return
    lastWrite = now
    localStorage.setItem(ACTIVITY_KEY, String(now))
  }

  const isIdle = () => {
    const last = readLastActivity()
    // No timestamp (first run, or cleared storage) counts as fresh activity —
    // the user just loaded the page.
    if (last === null) {
      touch()
      return false
    }
    return Date.now() - last >= timeoutMs
  }

  const check = () => {
    if (stopped) return
    if (isIdle()) {
      stop()
      onIdle()
    }
  }

  // Returning to a hidden tab should log out immediately if the timeout has
  // passed, rather than waiting for the next interval tick. If it hasn't,
  // switching back to the tab is itself activity.
  const onVisible = () => {
    if (document.hidden) return
    check()
    if (!stopped) touch()
  }

  const interval = setInterval(check, CHECK_INTERVAL_MS)
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, touch, { passive: true }),
  )
  document.addEventListener('visibilitychange', onVisible)

  function stop() {
    if (stopped) return
    stopped = true
    clearInterval(interval)
    ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, touch))
    document.removeEventListener('visibilitychange', onVisible)
  }

  touch()
  return stop
}
